import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import axios from 'axios'; // Ensure you run: npm install axios
import logo from '../assets/logo.png';

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    // UPDATED: Async handleLogin to connect to your MySQL backend
    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('http://localhost:5000/api/login', {
                email: email,
                password: password
            });

            if (response.data.Status === "Success") {
                // Store the JWT token for session persistence
                localStorage.setItem('token', response.data.Token);
                navigate('/store');
            }
        } catch (err) {
            // Handle specific error messages from your backend
            const errorMessage = err.response?.data?.Error || "Connection to server failed.";
            alert(errorMessage);
        }
    };

    return (
        <div className="min-h-screen relative flex items-center justify-center md:flex-row bg-boseth overflow-x-hidden font-sans">

            {/* MOBILE BACKGROUND */}
            <div className="absolute inset-0 md:hidden overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874?auto=format&fit=crop&q=80&w=2070"
                    alt="Background"
                    className="w-full h-full object-cover opacity-20 mix-blend-luminosity scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-boseth/90 via-boseth/70 to-boseth" />
            </div>

            {/* LEFT SIDE: FORM SECTION */}
            <div className="relative z-10 w-full md:w-1/2 min-h-screen flex flex-col justify-between p-6 md:p-12 lg:p-20 bg-white/95 md:bg-white backdrop-blur-sm md:backdrop-blur-none transition-all duration-500">

                {/* Branding: Static Logo with Motion Text */}
                <motion.div
                    className="flex items-center cursor-pointer group w-fit"
                    onClick={() => navigate('/')}
                    initial="initial"
                    whileHover="hover"
                >
                    <img
                        src={logo}
                        alt="Boseth Logo"
                        className="w-10 h-10 md:w-12 md:h-12 object-contain"
                    />
                    <motion.div
                        className="flex flex-col ml-3 border-l border-gray-200 pl-3"
                        variants={{
                            initial: { x: 0 },
                            hover: { x: 4 }
                        }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    >
                        <span className="font-serif text-lg md:text-xl tracking-[0.1em] text-boseth leading-none uppercase">Boseth</span>
                        <span className="text-[6px] md:text-[7px] tracking-[0.4em] uppercase text-gold font-bold mt-1">Wellness Portal</span>
                    </motion.div>
                </motion.div>

                {/* Main Login Box */}
                <div className="max-w-md w-full mx-auto py-12 md:py-0 animate-in fade-in slide-in-from-bottom-4 duration-1000">
                    <div className="mb-8 md:mb-12 text-center md:text-left">
                        <h2 className="text-4xl md:text-5xl font-serif text-boseth mb-4 tracking-tight">Staff Login</h2>
                        <p className="text-gray-500 text-sm font-light leading-relaxed max-w-xs mx-auto md:mx-0">
                            Authorized personnel only. Please sign in to access the management dashboard.
                        </p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-5 md:space-y-6">
                        {/* Email Input */}
                        <div className="space-y-1.5">
                            <label className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold ml-1">Email Address</label>
                            <div className="relative group">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-boseth transition-colors" size={18} />
                                <input
                                    type="email"
                                    placeholder="e.g. admin@boseth.com"
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50/80 border border-transparent rounded-xl outline-none focus:bg-white focus:border-gold/30 focus:ring-4 focus:ring-gold/5 transition-all text-sm text-boseth font-medium placeholder:font-light"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        {/* Password Input */}
                        <div className="space-y-1.5">
                            <label className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold ml-1">Secure Password</label>
                            <div className="relative group">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-boseth transition-colors" size={18} />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    className="w-full pl-12 pr-12 py-4 bg-gray-50/80 border border-transparent rounded-xl outline-none focus:bg-white focus:border-gold/30 focus:ring-4 focus:ring-gold/5 transition-all text-sm text-boseth font-medium placeholder:font-light"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-boseth transition-colors"
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between pt-1">
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <div className="relative flex items-center justify-center">
                                    <input type="checkbox" className="peer appearance-none w-4 h-4 border border-gray-200 rounded checked:bg-boseth checked:border-boseth transition-all cursor-pointer" />
                                    <div className="absolute text-white opacity-0 peer-checked:opacity-100 pointer-events-none text-[10px]">✓</div>
                                </div>
                                <span className="text-[10px] uppercase tracking-widest text-gray-400 group-hover:text-boseth transition font-bold">Remember Me</span>
                            </label>
                            <button type="button" className="text-[10px] text-gold tracking-widest font-black uppercase hover:text-boseth transition-colors">
                                Help?
                            </button>
                        </div>

                        <button className="w-full bg-boseth text-white py-4 md:py-5 mt-4 uppercase tracking-[0.3em] text-[10px] md:text-[11px] font-black hover:bg-gold hover:shadow-gold/20 transition-all duration-700 shadow-xl shadow-boseth/20 rounded-xl active:scale-[0.98]">
                            Authorize Entry
                        </button>
                    </form>
                </div>

                {/* Bottom Spacer/Copyright */}
                <div className="text-center md:text-left text-[8px] text-gray-300 uppercase tracking-widest py-4 md:py-0">
                    © 2026 Boseth Wellness Private Limited.
                </div>
            </div>

            {/* RIGHT SIDE: DESKTOP IMAGE VIEW */}
            <div className="hidden md:block md:w-1/2 relative bg-boseth h-screen">
                <img
                    src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874?auto=format&fit=crop&q=80&w=2070"
                    alt="Ayurveda Heritage"
                    className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-luminosity"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-boseth/20 to-boseth" />

                <div className="absolute inset-0 flex flex-col justify-end p-20 text-white">
                    <div className="space-y-6 max-w-lg animate-in fade-in slide-in-from-right-10 duration-1000">
                        <p className="text-gold uppercase tracking-[0.6em] text-[10px] font-black">Heritage Wellness</p>
                        <h3 className="text-6xl font-serif leading-[1.1] tracking-tight">Authentic Healing <br/>Through Tradition.</h3>
                        <div className="w-24 h-[1px] bg-gold/50" />
                        <p className="text-white/60 text-sm font-light tracking-wide leading-relaxed">
                            Managing the ancient Sri Lankan Hela Wedakama with modern luxury wellness systems.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}